import { useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { Link, useLocation } from "react-router";
import type { PublicMapDefinition } from "@fortex/api";
import { EmptyState, ForteXMap, Skeleton, decode, errorMessage } from "@fortex/map";
import type { ForteXMapConfig, MapCoreApi, Theme } from "@fortex/map";
import { TopPill } from "../app/AppMenu";
import { useApp } from "../app/context";
import { useLoad } from "../app/useLoad";
import { uiFeatures } from "../auth/store";
import { buildApi } from "../config";
import type { AppConfig } from "../config";
import type { AppLang } from "../i18n";
import { useMapAppSync } from "./MapPage";
import { canOfGrants, mapControlsFor } from "./mapFeatures";

export const publicMapParam = "publicmapid";

const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function publicMapIdOf(search: string): string | null {
  const value = new URLSearchParams(search.startsWith("?") ? search.substring(1) : search).get(publicMapParam);

  return value !== null && guidPattern.test(value.trim()) ? value.trim().toLowerCase() : null;
}

export type PublicMapConfigInput = { definition: PublicMapDefinition; search: string; lang: AppLang; theme: Theme; accent: string | null; nominatimUrl: string | null };

export function publicMapConfig(input: PublicMapConfigInput): ForteXMapConfig {
  const url = decode(input.search);
  const definition = input.definition;
  const can = canOfGrants(definition.features);
  const gated = mapControlsFor(can, definition.searchable, definition.searchLabel);
  const center = definition.lat !== null && definition.lon !== null ? ([definition.lat, definition.lon] as [number, number]) : undefined;

  return {
    lang: url.lang ?? input.lang,
    theme: url.theme ?? input.theme,
    chrome: url.chrome ?? "full",
    accent: input.accent,
    storageKey: `public:${definition.id}`,
    urlState: true,
    layers: { maps: definition.maps, layersOffered: definition.layers, defaultOn: definition.defaultOn },
    info: { ...gated.info, admin: false, layers: definition.searchable },
    draw: { editable: false },
    view: { center, zoom: definition.zoom ?? undefined },
    nominatimUrl: input.nominatimUrl,
    controls: gated.controls
  };
}

export function publicApi(config: AppConfig, lang: AppLang) {
  return buildApi({ config, lang, token: () => null, onUnauthorized: () => undefined });
}

export function PublicMapPage({ id }: { id: string }) {
  const { config, lang, theme, labels } = useApp();
  const location = useLocation();
  const api = useMemo(() => publicApi(config, lang), [config, lang]);
  const definition = useLoad(signal => api.publicMapDefinition(id, signal), [id]);
  const coreRef = useRef<MapCoreApi | null>(null);
  const [initial] = useState(() => ({ search: location.search, lang, theme }));
  const onReady = useMapAppSync(coreRef);
  const mapConfig = useMemo(() => (definition.data === null ? null : publicMapConfig({ definition: definition.data, search: initial.search, lang: initial.lang, theme: initial.theme, accent: config.accent, nominatimUrl: config.nominatimUrl })), [definition.data, initial, config]);
  const menu = definition.data !== null && canOfGrants(definition.data.features)(uiFeatures.menu, "ReadOnly");

  if (definition.error !== null && definition.data === null) {
    return (
      <div className="fxapp-map-page fxapp-public-map-empty" data-fxmap="public-map-missing">
        <EmptyState icon="warning" title={labels.publicMapNotFound} text={errorMessage(definition.error, labels)} action={<Link to="/login">{labels.signIn}</Link>} />
      </div>
    );
  }

  if (mapConfig === null || definition.data === null) {
    return (
      <div className="fxapp-map-page fxapp-public-map-empty" aria-busy="true" aria-label={labels.publicMapLoading} data-fxmap="public-map-loading">
        <Skeleton lines={3} />
      </div>
    );
  }

  return (
    <div className="fxapp-map-page" data-fxmap="public-map-page" data-public-map={definition.data.id}>
      <ForteXMap api={api} config={mapConfig} onReady={onReady} style={config.accent ? ({ "--fxmap-accent": config.accent } as CSSProperties) : undefined} />
      <div className="fxapp-map-chrome">
        <TopPill menu={menu} subtitle={definition.data.name} />
      </div>
    </div>
  );
}
